// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import handler from '../../../../share/util/apiResultValidator';
import customTable from '../../../../share/components/vue/PaginationTable';
import resource from '../../../../share/util/resourceMap';
import loading from '../../../../share/components/vue/ProcessingIcon';
import diagnoseResource from '../../../../share/util/diagnose';
import service from '../../../../share/util/api/service';

export default {
  name: "allCustomizePage",
  data() {
    return {
      processing: false,
      measuredDataColumns: [],
      measuredDataItems: [],
    };
  },

  watch: {
    language() {
      this.updateColumns();
    },
  },

  components: {
    'custom-table': customTable,
    'processing-view': loading,
  },

  methods: {
    ...mapActions('auth', ['logout']),

    /**
     * カスタマイズ計測データ一覧取得
     * @param {String} projectId プロジェクトID
     * @param {String} measuresId 計測データID
     */
    async createCstMeasuresList(projectId, measuresId) {
      try {
        this.processing = true;
        const params = { region_info: this.cachedProjectDataSource };
        const customizeEndPoint = '/customize/' + projectId + '/' + measuresId;
        const resCustomize = await service.getParamCall(service.CST, customizeEndPoint, params, this.operationScreen);

        this.measuredDataItems = [];
        handler.validate(handler.validateTypes.all, resCustomize, this, null, () => {
          this.updateColumns();
          this.getCustomizeTable(resCustomize.data.projects);
        }, (result) => {
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });
      } finally {
        this.processing = false;
      }
    },

    // カラム更新
    async updateColumns() {
      this.measuredDataColumns = [
        { name: 'systemName', label: 'message.header_vehicle_table_system_name' },
        { name: 'name', label: 'individual.header_customize_name' },
        { name: 'currentValue', label: 'individual.header_customize_current_setting' },
        { name: 'customizeWriteSettings', label: 'individual.header_customize_write_settings' },
        { name: 'result', label: 'individual.header_customize_result', useResource: true },
        { name: 'description', label: 'individual.common_label_description' },
      ];
    },

    /**
     * カスタマイズの一覧表示.
     * @param {Array} detailData レスポンスのデータ
     * @returns カスタマイズの計測データ一覧
     */
    getCustomizeTable(detailData) {
      if (!detailData || detailData.length < 1) {
        this.measuredDataItems = [];
      }

      detailData.forEach(element => {
        this.measuredDataItems = this.measuredDataItems.concat(element.results.map(ele => ({
          systemName: this.$t('diagSytem.' + element.system_name),                                                             // システム名
          name: this.$t(`cst${element.system_name}.${ele.immutable_id}.menu_title`),                                           // 名称
          currentValue: resource.convertCstmDisplayItem(ele.id, ele.setting.value, element.system_name, this, ele.data_type),                 // 現在値
          customizeWriteSettings: resource.convertCstmDisplayItem(ele.id, ele.setting.value, element.system_name, this, ele.data_type),       // 設定値
          result: this.getCustomizeResult(ele.customize_status),                                                               // 結果
          description: this.$te(`cst${element.system_name}.${ele.immutable_id}.menu_description`)
            ? this.$t(`cst${element.system_name}.${ele.immutable_id}.menu_description`) : '-',                                 // 説明
          bgStyle: 'dtc-cursor-default'
        })));
      });
    },

    /**
     * カスタマイズ結果表示文言作成.
     * @param {String} statusId ステータス
     * @returns ステータス表示文言
     */
    getCustomizeResult(statusId) {
      let status = '';
      switch (statusId) {
        case config.CSTM_STATUS_TYPE_SUCCESS:
          status = 'individual.status_customize_result_success';
          break;
        case config.CSTM_STATUS_TYPE_FAILURE:
          status = 'individual.status_customize_result_failure';
          break;
        case config.CSTM_STATUS_TYPE_ERROR:
          status = 'individual.status_customize_result_error';
          break;
        default:
          status = 'message.common_hyphen';
          break;
      }
      return status;
    },

    /**
     * メモボタン押下.
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async parentOpenMemoModal() {
      this.$emit('openMemoModal');
    },
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    }
  },
};